/**
 * SeizeSkillEffect — Lightning tree Pulse path Fork 2: Seize active.
 *
 * People become projectiles. Seize grabs a disrupted enemy's nervous system
 * and hurls them up to 3 tiles in any direction. The throw resolves through
 * a `ThrowSystem` dep — the skill provides config; the dep handles
 * trajectory, collision detection, and impact damage. The skill handles status
 * effects after the throw completes, based on the throw result.
 *
 * Requires a disrupted target in range. Fizzles on no valid target (or low
 * attunement XP). Cooldown starts regardless of fizzle type.
 *
 * On activation:
 *   1. rollSkillSuccess('seize') — fizzle at low attunement XP.
 *   2. Resolve throw distance: baseThrowTiles + getAdditiveEffect('seize', 'throwDistanceTiles').
 *   3. Resolve impact multiplier: getMultiplicativeEffect('seize', 'impactDamageMultiplier').
 *   4. Call throw.execute() with resolved config.
 *   5. If !targetAcquired → fizzle (no disrupted enemy in range).
 *   6. On wall impact:
 *      - Check immunity; apply stun (wallImpactStunMs) to thrown enemy.
 *      (Damage is handled inside throw.execute().)
 *   7. On enemy impact:
 *      - Check immunity; apply stun to thrown enemy.
 *      - If struckStatusEffects present: check immunity; apply stun to struck enemy.
 *      - Chain of Command (seize_passive): if collateralDisruptMs > 0 from resolver,
 *        apply disruption to struck enemy (chain cascade).
 *   8. Start cooldown.
 *
 * Disruption on the thrown enemy is NOT re-applied or reset during throw —
 * the timer continues ticking while the enemy travels. Seize does not call
 * statusEffects.apply('disruption', ...) on the thrown enemy.
 *
 * Upgrade key inheritance (resolver scope: 'seize'):
 * - throwDistanceTiles: additive — Marionette adds 1 tile (4 total).
 * - impactDamageMultiplier: multiplicative — Marionette multiplies by 1.4.
 * - collateralDisruptMs: additive — Chain of Command adds 800ms disruption
 *   to the struck enemy on body collision (returns 0 when not unlocked).
 *
 * Base values not in JSON (flagged in spec — resolved to spec recommendations):
 * - wallImpactDamage: 8 flat (spec § Seize: "recommend 8 damage base").
 * - wallImpactStunMs: 600ms (spec § Seize: stun applied on wall collision).
 * - bodyImpactDamage: 8 flat (spec § Seize: flat value, same as wall).
 * - bodyImpactStunMs: 600ms (spec § Seize: stun applied to both on body collision).
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-065 (CC hierarchy — stun has 300ms immunity window)
 * @see lightning-spec.md § Seize (`seize`)
 * @see lightning-spec.md § Marionette (`seize_upgrade_1`)
 * @see lightning-spec.md § Chain of Command (`seize_passive`)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Throw system types
// ---------------------------------------------------------------------------

/**
 * Config passed to the ThrowSystem when executing a Seize throw.
 * The throw system handles trajectory, wall/enemy collision detection,
 * and impact damage application to both thrown and struck enemies.
 */
export interface SeizeThrowConfig {
  /** Tiles the thrown enemy can travel before the throw resolves. */
  readonly maxDistanceTiles: number;
  /** Flat damage dealt to the thrown enemy on wall collision. */
  readonly wallImpactDamage: number;
  /** Flat damage dealt to both enemies on body collision. */
  readonly bodyImpactDamage: number;
  /** Source skill ID for damage attribution and resolver lookup. */
  readonly sourceSkillId: string;
}

export type SeizeImpactType = 'wall' | 'enemy' | 'none';

/**
 * Result returned by the ThrowSystem after executing the throw.
 * The skill uses this to decide which status effects to apply.
 */
export interface SeizeThrowResult {
  /**
   * True if a disrupted enemy was found in range and the throw executed.
   * False if no valid target — skill fizzles without starting cooldown.
   */
  readonly targetAcquired: boolean;
  /**
   * How the throw resolved.
   * - 'wall'  → thrown enemy struck a wall (bonus damage + stun).
   * - 'enemy' → thrown enemy struck another enemy (impact damage to both + stun both).
   * - 'none'  → throw completed without collision (enemy lands, no stun).
   */
  readonly impact: SeizeImpactType;
}

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface SeizeDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string): number;
  };
  /**
   * ThrowSystem interface — executes the physical throw including trajectory,
   * collision detection, and impact damage. Returns the throw result for
   * status effect application.
   */
  readonly throw: {
    execute(config: SeizeThrowConfig): SeizeThrowResult;
  };
  /**
   * Status effect interface for the thrown (primary) enemy.
   * Stun is applied here on wall or body collision.
   */
  readonly statusEffects: {
    apply(defId: string, opts: { sourceSkillId: string; durationMs: number }): void;
    /** True if the target is immune to the given status (stun has a 300ms immunity window). */
    isImmune(defId: string): boolean;
  };
  /**
   * Status effect interface for the struck (secondary) enemy.
   * Only relevant on 'enemy' impact. Stun and Chain of Command disruption go here.
   * Optional — omit in scenarios where only single-enemy mechanics are tested.
   */
  readonly struckStatusEffects?: {
    apply(defId: string, opts: { sourceSkillId: string; durationMs: number }): void;
    isImmune(defId: string): boolean;
  };
  readonly rollSkillSuccess: (skillId: string) => boolean;
  readonly breakHide?: () => void;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface SeizeConfig {
  /** Long cooldown tier: 1200ms (lightning tree). */
  readonly cooldownMs: number;
  /** Base throw distance in tiles. Spec: 3 tiles. */
  readonly baseThrowTiles: number;
  /**
   * Flat wall impact damage. Not in JSON — spec recommendation: 8.
   * Scaled by impactDamageMultiplier from the resolver (Marionette).
   */
  readonly wallImpactDamage: number;
  /**
   * Wall impact stun duration in ms. Not in JSON — spec recommendation: 600ms.
   * Applied to the thrown enemy if not stun-immune.
   */
  readonly wallImpactStunMs: number;
  /**
   * Flat body impact damage applied to both the thrown and struck enemies.
   * Not in JSON — flat value per spec; same base as wall impact damage.
   * Scaled by impactDamageMultiplier from the resolver (Marionette).
   */
  readonly bodyImpactDamage: number;
  /**
   * Body impact stun duration in ms. Applied to both enemies (immunity-gated).
   * Spec inference: same duration as wall impact stun (600ms).
   */
  readonly bodyImpactStunMs: number;
}

const DEFAULT_CONFIG: SeizeConfig = {
  cooldownMs: 1200,
  baseThrowTiles: 3,
  wallImpactDamage: 8,
  wallImpactStunMs: 600,
  bodyImpactDamage: 8,
  bodyImpactStunMs: 600,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class SeizeSkillEffect implements SkillEffect {
  readonly skillId = 'seize';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly missLabel = 'MISS';
  readonly failLabel = 'FIZZLE';

  private readonly deps: SeizeDeps;
  private readonly config: SeizeConfig;

  private _cooldownRemaining = 0;
  private _activations = 0;
  private _fizzles = 0;
  private _wallImpacts = 0;
  private _bodyImpacts = 0;
  /** Stun applications to the thrown (primary) enemy. */
  private _stunApplications = 0;
  /** Stun applications to the struck (secondary) enemy on body collision. */
  private _struckStunApplications = 0;
  /** Disruption applications to the struck enemy via Chain of Command. */
  private _collateralDisrupts = 0;
  private _resolvedThrowTiles = 0;
  private _resolvedWallDamage = 0;
  private _resolvedBodyDamage = 0;

  constructor(deps: SeizeDeps, config: Partial<SeizeConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters (for simulation assertions) ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activations(): number { return this._activations; }
  get fizzles(): number { return this._fizzles; }
  get wallImpacts(): number { return this._wallImpacts; }
  get bodyImpacts(): number { return this._bodyImpacts; }
  get stunApplications(): number { return this._stunApplications; }
  get struckStunApplications(): number { return this._struckStunApplications; }
  get collateralDisrupts(): number { return this._collateralDisrupts; }
  get resolvedThrowTiles(): number { return this._resolvedThrowTiles; }
  get resolvedWallDamage(): number { return this._resolvedWallDamage; }
  get resolvedBodyDamage(): number { return this._resolvedBodyDamage; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Fizzle check — low attunement XP produces visible effect but no throw.
    if (!this.deps.rollSkillSuccess('seize')) {
      this._fizzles++;
      return false;
    }

    this.deps.breakHide?.();

    // Resolve throw distance (Marionette: +1 tile additive).
    const throwDistanceBonus = this.deps.resolver.getAdditiveEffect('seize', 'throwDistanceTiles');
    this._resolvedThrowTiles = this.config.baseThrowTiles + throwDistanceBonus;

    // Resolve impact damage multiplier (Marionette: ×1.4 multiplicative).
    const impactMultiplier = this.deps.resolver.getMultiplicativeEffect('seize', 'impactDamageMultiplier');
    this._resolvedWallDamage = Math.round(this.config.wallImpactDamage * impactMultiplier);
    this._resolvedBodyDamage = Math.round(this.config.bodyImpactDamage * impactMultiplier);

    // Execute the throw — ThrowSystem handles trajectory, collision, and damage.
    const result = this.deps.throw.execute({
      maxDistanceTiles: this._resolvedThrowTiles,
      wallImpactDamage: this._resolvedWallDamage,
      bodyImpactDamage: this._resolvedBodyDamage,
      sourceSkillId: 'seize',
    });

    if (!result.targetAcquired) {
      // No disrupted enemy in range — fizzle. Cooldown does NOT start on a
      // no-target fizzle: the skill didn't fire, so no recovery needed.
      this._fizzles++;
      return false;
    }

    this._activations++;

    if (result.impact === 'wall') {
      this._wallImpacts++;
      // Apply stun to the thrown enemy (immunity-gated — stun has 300ms window).
      if (!this.deps.statusEffects.isImmune('stun')) {
        this.deps.statusEffects.apply('stun', {
          sourceSkillId: 'seize',
          durationMs: this.config.wallImpactStunMs,
        });
        this._stunApplications++;
      }
    } else if (result.impact === 'enemy') {
      this._bodyImpacts++;

      // Apply stun to the thrown (primary) enemy.
      if (!this.deps.statusEffects.isImmune('stun')) {
        this.deps.statusEffects.apply('stun', {
          sourceSkillId: 'seize',
          durationMs: this.config.bodyImpactStunMs,
        });
        this._stunApplications++;
      }

      // Apply stun to the struck (secondary) enemy.
      if (this.deps.struckStatusEffects) {
        if (!this.deps.struckStatusEffects.isImmune('stun')) {
          this.deps.struckStatusEffects.apply('stun', {
            sourceSkillId: 'seize',
            durationMs: this.config.bodyImpactStunMs,
          });
          this._struckStunApplications++;
        }

        // Chain of Command (seize_passive): disrupt the struck enemy.
        // getAdditiveEffect returns 0 when the passive is not unlocked,
        // preventing spurious disruption on un-upgraded Seize.
        const collateralDisruptMs = this.deps.resolver.getAdditiveEffect('seize', 'collateralDisruptMs');
        if (collateralDisruptMs > 0) {
          // STUB: deferred — no enemy ability/cast system reads disruption
          this.deps.struckStatusEffects.apply('disruption', {
            sourceSkillId: 'seize',
            durationMs: collateralDisruptMs,
          });
          this._collateralDisrupts++;
        }
      }
    }
    // 'none' impact — throw completes without collision; no stun applied.

    // Start cooldown.
    this._cooldownRemaining = this.config.cooldownMs;

    return true;
  }

  deactivate(): void {
    // Instant — no toggle state.
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
